import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useContactStatuses,
  contactStatusApi,
  CONTACT_STATUS_KEY,
  type ContactStatus,
  type RevealResult,
} from "@agentic_conversation/api/contact-status";

interface ContactStatusContextValue {
  statusFor: (userId: number) => ContactStatus | undefined;
  reveal: (userId: number) => Promise<RevealResult>;
}

const Ctx = createContext<ContactStatusContextValue | null>(null);

export function useContactStatus(userId?: number): ContactStatus | undefined {
  const ctx = useContext(Ctx);
  if (!ctx || !userId) return undefined;
  return ctx.statusFor(userId);
}

// Plain lookup (not a hook) so callers can resolve many users in a loop.
export function useStatusFor(): (userId: number) => ContactStatus | undefined {
  const ctx = useContext(Ctx);
  return ctx?.statusFor ?? (() => undefined);
}

// Returns the reveal action, or null when no provider is mounted (e.g. tests /
// surfaces without contact statuses) — callers fall back gracefully.
export function useReveal(): ((userId: number) => Promise<RevealResult>) | null {
  return useContext(Ctx)?.reveal ?? null;
}

// Extracts every insiti://…?user_id=<id> id from a blob of markdown.
const USER_ID_RE = /insiti:\/\/\w+\/\d+\?user_id=(\d+)/g;

export function extractUserIds(texts: Array<string | null | undefined>): number[] {
  const ids = new Set<number>();
  for (const text of texts) {
    if (!text) continue;
    for (const match of text.matchAll(USER_ID_RE)) {
      ids.add(parseInt(match[1], 10));
    }
  }
  return [...ids];
}

interface ProviderProps {
  // The markdown bodies to scan for profile links (one batch fetch for all).
  contents: Array<string | null | undefined>;
  children: ReactNode;
}

export function ContactStatusProvider({ contents, children }: ProviderProps) {
  const queryClient = useQueryClient();
  const userIds = useMemo(() => extractUserIds(contents), [contents]);
  const { data } = useContactStatuses(userIds);

  const value = useMemo<ContactStatusContextValue>(
    () => ({
      statusFor: (userId) => data?.[String(userId)],
      reveal: async (userId) => {
        const result = await contactStatusApi.reveal(userId);
        // Refresh every badge for this user, in any message — one shared cache.
        queryClient.invalidateQueries({ queryKey: CONTACT_STATUS_KEY });
        return result;
      },
    }),
    [data, queryClient],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
